/* app/components-gallery.jsx - Stage 2: core component library, all states.
   Built entirely from Huddle DS primitives + the Marketlens additions defined
   in marketlens.css. Reached via Design foundations → Components tab. */
(function () {
  const { useState, useRef } = React;
  function MlIcon(props) { return React.createElement(window.MlIcon, props); }

  // ---------- small reusable bits ----------
  function Spec({ title, note, addition, plain, children }) {
    return (
      <section className="ml-spec">
        <div className="ml-spec__head">
          <div className="ml-spec__title">{title}{addition && <span className="ml-addbadge" title="Proposed addition to the Huddle system">+ system addition</span>}</div>
          {note && <p className="ml-spec__note">{note}</p>}
        </div>
        <div className={"ml-spec__demo" + (plain ? " ml-spec__demo--plain" : "")}>{children}</div>
      </section>
    );
  }
  function Col({ label, children }) {
    return <div className="ml-speccol"><div className="ml-statelabel">{label}</div>{children}</div>;
  }

  function SeoGauge({ score, size = 96, label }) {
    const r = (size - 12) / 2, c = 2 * Math.PI * r;
    const tone = score >= 75 ? "success" : score >= 50 ? "warning" : "danger";
    const color = `var(--${tone})`;
    return (
      <div className="ml-gauge">
        <div className="ml-gauge__ring" style={{ width: size, height: size }}>
          <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--gray-200)" strokeWidth="8" />
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={c} strokeDashoffset={c * (1 - score/100)} style={{ transition: "stroke-dashoffset .5s cubic-bezier(.16,.84,.44,1)" }} />
          </svg>
          <div className="ml-gauge__num"><span className="ml-gauge__val">{score}</span><span className="ml-gauge__max">/ 100</span></div>
        </div>
        {label && <span className="ml-gauge__label" style={{ color }}>{label}</span>}
      </div>
    );
  }

  // ---------- Multi-step destructive delete flow ----------
  function DeleteFlow({ onClose }) {
    const { Button, Checkbox } = window.HuddleDesignSystem_775113;
    const [step, setStep] = useState(1);
    const [mode, setMode] = useState("trash");
    const [typed, setTyped] = useState("");
    const [ack, setAck] = useState(false);
    const steps = [
      { n: 1, name: "Confirm" },
      { n: 2, name: "Trash or delete" },
      { n: 3, name: "Type to confirm" },
    ];
    const canFinish = typed === "DELETE" && ack;
    const indicator = (
      <div className="ml-steps">
        {steps.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className={["ml-step", step === s.n ? "ml-step--active" : "", step > s.n ? "ml-step--done" : "", s.n === 3 ? "ml-step--danger" : ""].filter(Boolean).join(" ")}>
              <span className="ml-step__dot">{step > s.n ? <MlIcon name="check" size={13} /> : s.n}</span>
              <span className="ml-step__name">{s.name}</span>
            </div>
            {i < steps.length - 1 && <span className="ml-step__line" />}
          </React.Fragment>
        ))}
      </div>
    );
    let body, footer;
    if (step === 1) {
      body = <div style={{ paddingBottom: 16 }}>{indicator}<p style={{ margin: 0, fontSize: "var(--text-sm)", color: "var(--text-secondary)", lineHeight: 1.6 }}>You're about to remove the post <strong>“Competitor teardown: how we map keyword overlap”</strong>. Its public URL will stop working immediately. You'll choose how permanent this is on the next step.</p></div>;
      footer = <><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="danger" onClick={() => setStep(2)}>Continue</Button></>;
    } else if (step === 2) {
      body = (
        <div style={{ paddingBottom: 16 }}>{indicator}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <label className={"ml-lifecard"} style={{ flexDirection: "row", alignItems: "flex-start", gap: 12, cursor: "pointer", borderColor: mode === "trash" ? "var(--primary)" : undefined, boxShadow: mode === "trash" ? "0 0 0 3px var(--primary-tint)" : undefined }}>
              <input type="radio" name="delmode" checked={mode === "trash"} onChange={() => setMode("trash")} style={{ marginTop: 3 }} />
              <span><span style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: "var(--text-sm)" }}>Move to Trash</span><br /><span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>Recoverable for 30 days. The booking link breaks but content is kept.</span></span>
            </label>
            <label className={"ml-lifecard"} style={{ flexDirection: "row", alignItems: "flex-start", gap: 12, cursor: "pointer", borderColor: mode === "perm" ? "var(--danger)" : undefined, boxShadow: mode === "perm" ? "0 0 0 3px var(--danger-surface)" : undefined }}>
              <input type="radio" name="delmode" checked={mode === "perm"} onChange={() => setMode("perm")} style={{ marginTop: 3 }} />
              <span><span style={{ fontWeight: 600, color: "var(--danger)", fontSize: "var(--text-sm)" }}>Delete permanently</span><br /><span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>Cannot be undone. Content, revisions and SEO data are erased.</span></span>
            </label>
          </div>
        </div>
      );
      footer = <><Button variant="ghost" onClick={() => setStep(1)}>Back</Button>{mode === "trash" ? <Button variant="danger" onClick={onClose}>Move to Trash</Button> : <Button variant="danger" onClick={() => setStep(3)}>Continue</Button>}</>;
    } else {
      body = (
        <div style={{ paddingBottom: 16 }}>{indicator}
          <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "0 0 14px" }}>This is irreversible. Type <span className="ml-confirm-strong">DELETE</span> to confirm.</p>
          <input className={"hds-input" + (typed && typed !== "DELETE" ? " hds-input--invalid" : "")} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" style={{ marginBottom: 14 }} />
          <Checkbox label="I understand this permanently deletes the post and its SEO history." checked={ack} onChange={(e) => setAck(e.target.checked)} />
        </div>
      );
      footer = <><Button variant="ghost" onClick={() => setStep(2)}>Back</Button><Button variant="danger" disabled={!canFinish} onClick={onClose} leadingIcon="trash">Delete permanently</Button></>;
    }
    return React.createElement(window.HuddleDesignSystem_775113.Dialog, {
      title: "Delete post", icon: "trash", iconTone: "danger", onClose, footer,
    }, body);
  }

  // ---------- Tabbed dialog ----------
  function TabbedDialog({ onClose }) {
    const { Button, Field, Input, Select, Switch } = window.HuddleDesignSystem_775113;
    const [tab, setTab] = useState("general");
    return React.createElement(window.HuddleDesignSystem_775113.Dialog, {
      title: "Site settings", description: "Marketlens US · marketlens.com", onClose,
      footer: <><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" onClick={onClose}>Save changes</Button></>,
    }, (
      <div style={{ paddingBottom: 16 }}>
        <div className="hds-tabs" style={{ marginBottom: 18 }}>
          {[["general","General"],["seo","SEO defaults"],["ai","AI"]].map(([id,l]) => (
            <button key={id} className={"hds-tab " + (tab===id?"hds-tab--active":"")} onClick={() => setTab(id)}>{l}</button>
          ))}
        </div>
        {tab === "general" && <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field label="Site name"><Input defaultValue="Marketlens US" /></Field>
          <Field label="Time zone"><Select defaultValue="et"><option value="et">Eastern (ET)</option><option value="pt">Pacific (PT)</option></Select></Field>
        </div>}
        {tab === "seo" && <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field label="Title separator" hint="Used between the post title and site name."><Select defaultValue="pipe"><option value="pipe">|</option><option value="dash">-</option></Select></Field>
          <Switch label="Auto-generate meta descriptions with AI" defaultChecked />
        </div>}
        {tab === "ai" && <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field label="Default model"><Select defaultValue="sonnet"><option value="sonnet">Claude Sonnet 4.5</option><option value="haiku">Claude Haiku 4.5</option></Select></Field>
          <Switch label="Require human review before publish" defaultChecked />
        </div>}
      </div>
    ));
  }

  // ---------- main screen ----------
  function Components({ onBack, onNav }) {
    const DS = window.HuddleDesignSystem_775113;
    const { Button, IconButton, Badge, StatCard, EmptyState, Banner, Toast, Field, Input, Select, Textarea, Checkbox, Radio, Switch } = DS;
    const { RefHeader } = window.MlRef;

    const [chips, setChips] = useState(["seo", "saas"]);
    const toggleChip = (id) => setChips(c => c.includes(id) ? c.filter(x => x !== id) : [...c, id]);
    const [life, setLife] = useState("validated");
    const [dz, setDz] = useState(false);
    const [dialog, setDialog] = useState(null); // 'basic' | 'tabbed' | 'delete'
    const [toasts, setToasts] = useState([]);
    const pushToast = (t) => { const id = Date.now() + Math.random(); setToasts(x => [...x, { ...t, id }]); setTimeout(() => setToasts(x => x.filter(y => y.id !== id)), 4200); };

    return (
      <div className="ml-doc">
        <RefHeader view="components" onNav={onNav} onBack={onBack} eyebrow="Stage 2 · Component library" title="Core components" />
        <p className="ml-doc__lede" style={{ marginBottom: 32 }}>Every control Marketlens ships, with its states. Items marked <span className="ml-addbadge">+ system addition</span> extend Huddle with primitives the platform needs (AI accent, gauge, dropzone, chat, lifecycle cards) - all built from the base tokens, ready to fold back into the system.</p>

        <div className="ml-gallery">

          {/* BUTTONS */}
          <Spec title="Buttons" note="One primary action per section. Verbs, sentence case. AI actions use the soft-indigo accent and always pair with the sparkles glyph.">
            <div className="ml-specrow">
              <Button variant="primary" leadingIcon="plus">New post</Button>
              <Button variant="secondary">Cancel</Button>
              <Button variant="ai" leadingIcon={<MlIcon name="sparkles" size={18} />}>AI Fill SEO</Button>
              <Button variant="danger" leadingIcon="trash">Delete</Button>
              <Button variant="ghost" leadingIcon="filter">Filters</Button>
              <Button variant="link">View docs</Button>
            </div>
            <div className="ml-specrow">
              <Button size="sm">Small</Button>
              <Button size="md">Medium</Button>
              <Button size="lg">Large</Button>
              <Button leadingIcon={<span className="ml-spinner ml-spinner--sm ml-spinner--on-primary" />} disabled>Saving…</Button>
              <Button variant="ai" leadingIcon={<span className="ml-spinner ml-spinner--sm ml-spinner--ai" />} disabled>Generating…</Button>
              <Button variant="primary" disabled>Disabled</Button>
            </div>
            <div className="ml-specrow">
              <IconButton icon="edit" label="Edit" />
              <IconButton icon="bell" label="Notifications" bordered />
              <IconButton icon="more-horizontal" label="More" />
              <IconButton icon="trash" label="Delete" />
            </div>
          </Spec>

          {/* FORM CONTROLS */}
          <Spec title="Form controls" note="Labelled fields with hint and error slots. 16px inputs, 8px radius, visible focus ring, clear invalid + disabled states.">
            <div className="ml-specgrid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
              <Field label="Focus keyword" required htmlFor="f1" hint="The primary term this post should rank for."><Input id="f1" leadingIcon="search" defaultValue="content operations" /></Field>
              <Field label="Slug" htmlFor="f2" error="A post with this slug already exists."><Input id="f2" defaultValue="/content-ops" invalid /></Field>
              <Field label="Status" htmlFor="f3"><Select id="f3" defaultValue="draft"><option value="draft">Draft</option><option value="review">In review</option><option value="published">Published</option></Select></Field>
              <Field label="Read-only" htmlFor="f4" hint="Disabled field"><Input id="f4" defaultValue="marketlens.com" disabled /></Field>
            </div>
            <div className="ml-specrow" style={{ marginTop: 20 }}>
              <Field label="Meta description" htmlFor="f5" hint="150-160 characters" className="" ><Textarea id="f5" rows={2} defaultValue="A practical guide to running content operations across a multi-site network." style={{ width: 420, maxWidth: "100%" }} /></Field>
            </div>
            <div className="ml-specrow" style={{ marginTop: 20, gap: 28 }}>
              <Checkbox label="Require human review" defaultChecked />
              <Checkbox label="Disabled option" disabled />
              <Radio name="vis" label="Public" defaultChecked />
              <Radio name="vis" label="Private" />
              <Switch label="Auto-publish translations" />
              <Switch label="Web search" defaultChecked />
            </div>
          </Spec>

          {/* BADGES + TOKENS */}
          <Spec title="Badges, count pills, status & relationship tokens" note="Reserved, meaningful pills. Status never relies on color alone - a dot or icon always accompanies the label.">
            <div className="ml-specrow">
              <Badge tone="success" dot>Published</Badge>
              <span className="hds-badge hds-badge--draft"><span className="hds-badge__dot" />Draft</span>
              <span className="hds-badge hds-badge--info"><span className="hds-badge__dot" />In review</span>
              <Badge tone="warning" dot>Pending</Badge>
              <Badge tone="danger" dot>Failed</Badge>
              <Badge tone="neutral">Archived</Badge>
              <span className="hds-badge hds-badge--ai"><MlIcon name="sparkles" size={12} />AI-assisted</span>
              <Badge tone="primary">12</Badge>
            </div>
            <div className="ml-specrow">
              <span className="hds-badge hds-badge--rel-look">Lookalike</span>
              <span className="hds-badge hds-badge--rel-comp">Complementary</span>
              <span className="hds-badge hds-badge--rel-alt">Alternative</span>
              <span className="ml-ours"><svg viewBox="0 0 24 24"><path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>Ours</span>
            </div>
          </Spec>

          {/* STAT + LIFECYCLE CARDS */}
          <Spec title="Stat cards & clickable lifecycle cards" addition note="KPI cards for dashboards; clickable lifecycle cards double as filters across the BI modules (Pending / Validated / Archived).">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 16, marginBottom: 20 }}>
              <StatCard label="Indexed pages" value="1,284" icon="file-text" delta="6%" deltaDirection="up" deltaNote="vs last month" />
              <StatCard label="Avg. position" value="14.2" icon="trending-up" delta="2.1" deltaDirection="up" deltaNote="GSC" />
              <StatCard label="Drafts in review" value="9" icon="edit" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, maxWidth: 560 }}>
              {[["pending","Pending","var(--warning)","18"],["validated","Validated","var(--success)","42"],["archived","Archived","var(--text-muted)","7"]].map(([id,l,c,v]) => (
                <button key={id} className={"ml-lifecard " + (life===id?"ml-lifecard--selected":"")} onClick={() => setLife(id)}>
                  <div className="ml-lifecard__top"><span className="ml-lifecard__dot" style={{ background: c }} /><span className="ml-lifecard__label">{l}</span></div>
                  <span className="ml-lifecard__value">{v}</span>
                </button>
              ))}
            </div>
          </Spec>

          {/* CHIPS */}
          <Spec title="Selectable chips" addition note="Toggle filters and keyword tokens. Used for secondary keywords, intents and analysis filters.">
            <div className="ml-chipset">
              {[["seo","SEO"],["saas","SaaS"],["content","Content ops"],["wordpress","WordPress"],["analytics","Analytics"]].map(([id,l]) => (
                <button key={id} className={"ml-chip " + (chips.includes(id)?"ml-chip--selected":"")} onClick={() => toggleChip(id)}>
                  {chips.includes(id) && <MlIcon name="check" size={14} />}{l}
                </button>
              ))}
              <button className="ml-chip ml-chip--ai ml-chip--selected"><MlIcon name="sparkles" size={14} />AI suggested</button>
            </div>
            <div className="ml-chipset" style={{ marginTop: 14 }}>
              <span className="ml-chip">programmatic seo<span className="ml-chip__x"><MlIcon name="x" size={14} /></span></span>
              <span className="ml-chip">keyword clusters<span className="ml-chip__x"><MlIcon name="x" size={14} /></span></span>
            </div>
          </Spec>

          {/* SEO GAUGE */}
          <Spec title="SEO score gauge" addition note="0-100 score with tips. Color shifts from red → amber → green by band. The centrepiece of the editor's SEO panel.">
            <div style={{ display: "flex", gap: 40, alignItems: "center", flexWrap: "wrap" }}>
              <SeoGauge score={86} label="Good" />
              <SeoGauge score={64} label="Needs work" />
              <SeoGauge score={41} label="Poor" />
              <ul className="ml-tips">
                <li className="is-ok"><MlIcon name="check-circle" size={16} />Focus keyword in title &amp; first paragraph</li>
                <li className="is-ok"><MlIcon name="check-circle" size={16} />Meta description within 150-160 characters</li>
                <li className="is-warn"><MlIcon name="alert-triangle" size={16} />Add 2 more internal links</li>
                <li className="is-bad"><MlIcon name="x-circle" size={16} />No image alt text on 3 images</li>
              </ul>
            </div>
          </Spec>

          {/* PROGRESS + SPINNERS */}
          <Spec title="Progress &amp; spinners" addition note="Determinate for known work (uploads, generation steps); indeterminate for unknown waits. AI work uses the indigo bar.">
            <div style={{ display: "flex", flexDirection: "column", gap: 18, maxWidth: 520 }}>
              <div className="ml-progress-row"><div className="ml-progress"><div className="ml-progress__bar" style={{ width: "72%" }} /></div><span className="ml-progress-row__pct">72%</span></div>
              <div className="ml-progress-row"><div className="ml-progress"><div className="ml-progress__bar ml-progress__bar--ai" style={{ width: "45%" }} /></div><span className="ml-progress-row__pct">45%</span></div>
              <div className="ml-progress-row"><div className="ml-progress ml-progress--indeterminate"><div className="ml-progress__bar ml-progress__bar--ai" /></div><span className="ml-progress-row__pct" style={{ color: "var(--text-muted)" }}>…</span></div>
              <div className="ml-specrow"><span className="ml-spinner" /><span className="ml-spinner ml-spinner--lg" /><span className="ml-spinner ml-spinner--ai ml-spinner--lg" /><span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>Fetching from web…</span></div>
            </div>
          </Spec>

          {/* DROPZONE */}
          <Spec title="Drag-drop upload zone" addition note="Used by keyword import (Excel/CSV). Hover to highlight; shows parsed-file state after drop.">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div className={"ml-dropzone" + (dz ? " ml-dropzone--active" : "")}
                   onMouseEnter={() => setDz(true)} onMouseLeave={() => setDz(false)}>
                <span className="ml-dropzone__icon"><MlIcon name="upload-cloud" size={22} /></span>
                <div className="ml-dropzone__title">Drop your file or <b>browse</b></div>
                <div className="ml-dropzone__hint">.xlsx or .csv · up to 10 MB · {dz ? "release to upload" : "hover to preview active state"}</div>
              </div>
              <div className="ml-dropzone" style={{ borderStyle: "solid", borderColor: "var(--success-border)", background: "var(--success-surface)", cursor: "default" }}>
                <span className="ml-dropzone__icon" style={{ color: "var(--success)" }}><MlIcon name="check-circle" size={22} /></span>
                <div className="ml-dropzone__title">keywords-q3.xlsx</div>
                <div className="ml-dropzone__hint">428 rows parsed · ready to map columns</div>
              </div>
            </div>
          </Spec>

          {/* BANNERS */}
          <Spec title="Inline banners" note="Page-level notices. Factual and recoverable, no blame.">
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Banner tone="warning" title="Verify your email">Publishing is paused until you confirm maya@imyboss.com.</Banner>
              <Banner tone="info" title="Draft-first">Every save lands in Draft. Publishing is always an explicit action.</Banner>
              <Banner tone="danger" title="Couldn't connect to Google Search Console">Re-authorize the connection to resume performance data.</Banner>
              <Banner tone="success" title="Connected">WordPress network discovered 5 sites.</Banner>
            </div>
          </Spec>

          {/* TOASTS */}
          <Spec title="Toasts &amp; guardrail responses" note="Confirmations, errors, and the platform's guardrail responses: 202 pending-approval, 403 agency-forbidden, and a non-blocking over-quota warning.">
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18, maxWidth: 380 }}>
              <Toast tone="success" title="Post published" message="“Internal linking at scale” is now live." />
              <Toast tone="info" title="202 · Pending approval" message="Action captured - awaiting client approval." />
              <Toast tone="danger" title="403 · Not permitted" message="Agency policy blocks this action for client sites." />
              <Toast tone="warning" title="Over plan limit" message="Marketlens ES is above its page cap. Publishing still works." />
            </div>
            <div className="ml-specrow">
              <Button variant="secondary" size="sm" onClick={() => pushToast({ tone: "success", title: "Saved to Draft", message: "Your changes are saved." })}>Trigger success</Button>
              <Button variant="secondary" size="sm" onClick={() => pushToast({ tone: "info", title: "202 · Pending approval", message: "Captured - awaiting client approval." })}>Trigger 202</Button>
              <Button variant="secondary" size="sm" onClick={() => pushToast({ tone: "warning", title: "Over plan limit", message: "Site is above its page cap." })}>Trigger quota</Button>
            </div>
          </Spec>

          {/* EMPTY STATES */}
          <Spec title="Empty states" note="Two sentences max: what this is + how to start.">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div className="hds-card"><EmptyState icon="inbox" title="Your inbox is clear" description="Paste research or fetch a URL and Marketlens will summarise it into a draft." actions={<Button variant="primary" size="sm" leadingIcon="plus">Add to inbox</Button>} /></div>
              <div className="hds-card"><EmptyState icon="target" title="No keywords yet" description="Import a spreadsheet or add keywords manually to start building your library." actions={<Button variant="secondary" size="sm" leadingIcon="download">Import</Button>} /></div>
            </div>
          </Spec>

          {/* DIALOGS */}
          <Spec title="Dialogs &amp; modals" note="Centred modal over a scrim. Includes a tabbed variant and the three-step destructive delete flow.">
            <div className="ml-specrow">
              <Button variant="secondary" onClick={() => setDialog("basic")}>Open dialog</Button>
              <Button variant="secondary" onClick={() => setDialog("tabbed")}>Open tabbed dialog</Button>
              <Button variant="danger" leadingIcon="trash" onClick={() => setDialog("delete")}>Delete flow (3 steps)</Button>
            </div>
          </Spec>

          {/* CHAT */}
          <Spec title="Chat bubbles, typing indicator &amp; composer" addition note="The AI Assistant surface. Assistant messages can carry context chips; the composer streams a typing indicator while generating.">
            <div style={{ maxWidth: 600, display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="ml-chat">
                <div className="ml-msg ml-msg--user">
                  <span className="ml-msg__avatar ml-msg__avatar--user">MR</span>
                  <div className="ml-bubble ml-bubble--user">Which competitors are we losing keyword overlap to this quarter?</div>
                </div>
                <div className="ml-msg">
                  <span className="ml-msg__avatar ml-msg__avatar--ai"><MlIcon name="sparkles" size={15} /></span>
                  <div className="ml-bubble ml-bubble--ai">Based on your BI graph, three competitors gained overlap on your core clusters. I can open a Competitors Analysis for the top one.</div>
                </div>
                <div className="ml-msg">
                  <span className="ml-msg__avatar ml-msg__avatar--ai"><MlIcon name="sparkles" size={15} /></span>
                  <div className="ml-bubble ml-bubble--ai"><span className="ml-typing"><span></span><span></span><span></span></span></div>
                </div>
              </div>
              <div className="ml-composer">
                <textarea rows={1} placeholder="Ask about your sites, keywords or competitors…" defaultValue=""></textarea>
                <Button className="ml-composer__send" variant="primary" size="sm" aria-label="Send" leadingIcon={<MlIcon name="send" size={16} />} />
              </div>
              <div className="ml-composer__ctx">
                <span className="ml-chip ml-chip--selected" style={{ height: 26 }}><MlIcon name="globe" size={13} />Marketlens US</span>
                <span className="ml-chip" style={{ height: 26 }}><MlIcon name="flag" size={13} />3 competitors</span>
              </div>
            </div>
          </Spec>

          {/* PROSE / LEGAL */}
          <Spec title="Long-form reading layout &amp; Markdown style" note="Docs and legal pages: a sticky table-of-contents rail beside a constrained prose column. The same prose style renders in-app help (Markdown).">
            <div className="ml-legal">
              <nav className="ml-legal__toc">
                <a href="#" className="is-active" onClick={(e)=>e.preventDefault()}>1. Overview</a>
                <a href="#" onClick={(e)=>e.preventDefault()}>2. Data we process</a>
                <a href="#" onClick={(e)=>e.preventDefault()}>3. Sub-processors</a>
                <a href="#" onClick={(e)=>e.preventDefault()}>4. Your rights</a>
              </nav>
              <div className="ml-prose">
                <div className="ml-legal__updated">Last updated 13 June 2026</div>
                <h2>1. Overview</h2>
                <p>Marketlens connects to your WordPress network and Google Search Console to power content operations and SEO. This page explains what we process and how the AI features handle your content.</p>
                <p>Marketlens is <strong>AI-assisted, not AI-autonomous</strong>: every suggestion is reviewed by a human before it persists. Use the <code>Validate</code> action to promote an entity from pending.</p>
                <h3>Data we never store</h3>
                <ul>
                  <li>Your Anthropic API key is encrypted at rest and never logged.</li>
                  <li>Guest-submitted content is processed in-region only.</li>
                </ul>
                <blockquote>Bookings and publishing are paused while your email is unverified.</blockquote>
              </div>
            </div>
          </Spec>

          {/* SHELL PRIMITIVES reference */}
          <Spec title="Shell primitives" note="The site-switcher dropdown and acting-as-tenant banner are documented and live in Stage 1's shell - switch back to the app to exercise them.">
            <div className="ml-impersonation" style={{ position: "static", borderRadius: "var(--radius-md)" }}>
              <MlIcon name="eye" size={16} />
              <span>You are acting as <strong>Acme Co · agency client</strong>. Changes are attributed to this tenant.</span>
              <span className="ml-impersonation__spacer" />
              <button className="ml-impersonation__exit"><MlIcon name="x" size={14} /> Exit tenant view</button>
            </div>
          </Spec>

        </div>

        {dialog === "basic" && React.createElement(DS.Dialog, { title: "Copy booking link?", description: "Anyone with this link can book time on Marketlens US.", icon: "link", onClose: () => setDialog(null), footer: <><Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button><Button variant="primary" onClick={() => setDialog(null)}>Copy link</Button></> }, <div style={{ paddingBottom: 16 }}><div className="hds-copylink"><span className="hds-copylink__icon"><MlIcon name="link" size={16} /></span><span className="hds-copylink__text">marketlens.com/r/intro-call</span><button className="hds-copylink__btn"><MlIcon name="copy" size={15} />Copy</button></div></div>)}
        {dialog === "tabbed" && <TabbedDialog onClose={() => setDialog(null)} />}
        {dialog === "delete" && <DeleteFlow onClose={() => setDialog(null)} />}

        <div className="hds-toast-region">
          {toasts.map(t => <Toast key={t.id} tone={t.tone} title={t.title} message={t.message} onClose={() => setToasts(x => x.filter(y => y.id !== t.id))} />)}
        </div>
      </div>
    );
  }

  window.MlComponents = { Components };
})();
